import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { isEmpty, sortBy } from 'lodash-es'

import { getInitials } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import Avatar from '@lyrahealth-inc/ui-core/lib/atoms/avatar/Avatar'
import RefreshButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/refreshButton/RefreshButton'
import Label from '@lyrahealth-inc/ui-core/lib/atoms/label/Label'
import Dashlet from '@lyrahealth-inc/ui-core/lib/molecules/layouts/dashlet/Dashlet'
import { IconBadge } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/iconBadge/IconBadge'
import { BadgeShape, BadgeVariant } from '@lyrahealth-inc/ui-core-crossplatform/src/badge/constants'
import {
  RISK_BADGE_TEXT,
  SELF_HARM_BADGE_TEXT,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/profileCardBadge/ProfileCardBadge'

import styles from './messagesDashlet.module.scss'
import { ProviderPairDashletItem } from './ProviderPairDashletItem'
import { CLIENT_HOME, MESSENGER } from '../../../common/constants/routingConstants'
import { getClientFullName } from '../../../common/utils/utils'
import { useClientRiskFlags } from '../../../dashboard/openItems/hooks/useClientRiskFlags'
import { getAuthConfig, getAuthUserId } from '../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../data/store'
import { setLastConversationClientId } from '../../../data/userSettingsSlice'
import { useIsEngageDAProvider } from '../../../hooks/useIsEngageDAProvider'
import { setSelectedClientId } from '../../clients/clientDetails/data/ltClientSlice'
import { useGetEpisodeParticipantsQuery } from '../../episodes/data/episodesApi'
import { ChatEntryPoint } from '../../messages/BaseChat'
import { getAnchorId } from '../../messages/data/conversationGrouping'
import { useConversationsData } from '../../messages/data/hooks/useConversationsData'
import { useProviderPairInboxConversations } from '../../messages/data/hooks/useProviderPairConversations'
import {
  clearSelectedConversation,
  setPendingInitialThreadId,
  setSelectedConversation,
} from '../../messages/data/messagesSlice'
import { fallbackProviderPairProfile } from '../../messages/data/providerPairCareHistory'
import { buildProviderPairThread, getProviderPairProfile } from '../../messages/data/providerPairUtils'
import { MessengerParticipant } from '../../messages/data/types'
import { getConversationsUnreadCount } from '../../messages/data/utils'
import MessagesPopover from '../../messages/MessagesPopover'
import { isProviderConversation } from '../dashboardUtils'
import { useDashboardClientsData } from '../hooks/useDashboardClientsData'
export const MessagesDashlet: React.FC = () => {
  const {
    clients,
    inactiveClients,
    includeInteractedDBTTeensClients,
    primaryClients,
    isLoading: isLoadingClients,
    isSuccess: clientsFetched,
  } = useDashboardClientsData()
  const providerId = useSelector(getAuthUserId)
  const showOverdueMessageFlag = useSelector(getAuthConfig)?.showOverdueMessageFlag ?? false
  const [msgPopoverOpen, setMsgPopoverOpen] = useState(false)
  const [initialMessageParticipant, setInitialMessageParticipant] = useState(MessengerParticipant.PATIENT)
  const navigate = useNavigate()
  const { isDBTTeensEnabled, isPreferredNameEnabled } = useFlags()
  const dispatch = useAppDispatch()
  const atRiskClientIds = useClientRiskFlags()
  const isDAProvider = useIsEngageDAProvider()
  const riskBadgeText = isDAProvider ? RISK_BADGE_TEXT : SELF_HARM_BADGE_TEXT

  const {
    data: conversations,
    isFetching: isLoadingConversations,
    isSuccess: conversationsFetched,
    refetch: refetchConversations,
  } = useConversationsData()
  // Gated by useProviderPairMessagingAccess, so providers without P<>P access never fetch or see these.
  const {
    currentData: providerPairConversations,
    isLoading: isLoadingProviderPairConversations,
    isFetching: isFetchingProviderPairConversations,
    isSuccess: providerPairConversationsFetched,
    isUninitialized: isProviderPairQuerySkipped,
    refetch: refetchProviderPairConversations,
  } = useProviderPairInboxConversations()
  const isRefreshingMessages =
    (isLoadingConversations && conversationsFetched) ||
    (isFetchingProviderPairConversations && providerPairConversationsFetched)
  const unreadProviderPairConversations = useMemo(
    () => providerPairConversations?.filter((conversation) => (conversation.current_user.unread_count ?? 0) > 0) ?? [],
    [providerPairConversations],
  )

  useEffect(() => {
    return () => {
      dispatch(clearSelectedConversation())
    }
  }, [dispatch])

  const dashboardConversations = useMemo(
    () =>
      includeInteractedDBTTeensClients
        ? conversations?.filter((conversation) => isProviderConversation(conversation, providerId))
        : conversations,
    [conversations, includeInteractedDBTTeensClients, providerId],
  )
  const hasUnreadDBTTeensCaregiverConversation = useMemo(
    () =>
      Boolean(
        isDBTTeensEnabled &&
        dashboardConversations?.some((conversation) => {
          if (!conversation.conversation_attributes.unread_provider_messages_count) {
            return false
          }
          const client = clients.find(({ id }) => id === getAnchorId(conversation))
          return (
            client?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens &&
            conversation.anchor_patient_lyra_id != null &&
            conversation.anchor_patient_lyra_id !== conversation.patient_lyra_id
          )
        }),
      ),
    [clients, dashboardConversations, isDBTTeensEnabled],
  )
  const { data: episodeParticipantRosters = [], isLoading: isLoadingEpisodeParticipantRosters } =
    useGetEpisodeParticipantsQuery(
      { providerId },
      {
        skip: !providerId || (!hasUnreadDBTTeensCaregiverConversation && unreadProviderPairConversations.length === 0),
      },
    )
  const caregiverFirstNames = useMemo(() => {
    const names = new Map<string, string>()
    episodeParticipantRosters.forEach(({ anchor_patient_lyra_id, participants }) => {
      participants.forEach(({ lyra_id, first_name }) => {
        if (first_name) {
          names.set(`${anchor_patient_lyra_id}:${lyra_id}`, first_name)
        }
      })
    })
    return names
  }, [episodeParticipantRosters])
  // Counterpart names come from the roster, falling back to the messenger's generic label.
  // Like provider-client rows, skip a conversation whose client isn't on the dashboard, in both the count and the list.
  const unreadProviderPairThreads = useMemo(
    () =>
      unreadProviderPairConversations.flatMap((conversation) => {
        const client = clients.find(({ id }) => id === conversation.patient_lyra_id)
        const thread = buildProviderPairThread(conversation, (counterpartId) =>
          fallbackProviderPairProfile(
            counterpartId,
            getProviderPairProfile(episodeParticipantRosters, conversation.patient_lyra_id, counterpartId)?.displayName,
          ),
        )
        return client && thread
          ? [{ thread, client, lastMessageSentAt: conversation.conversation_last_message_sent_datetime }]
          : []
      }),
    [clients, episodeParticipantRosters, unreadProviderPairConversations],
  )
  const primaryClientIds = useMemo(() => new Set(primaryClients.map(({ id }) => id)), [primaryClients])
  const unreadCount =
    getConversationsUnreadCount(dashboardConversations) +
    unreadProviderPairThreads.reduce((total, { thread }) => total + thread.unreadCount, 0)
  const unreadItems = useMemo(
    () =>
      sortBy(
        [
          ...(dashboardConversations ?? [])
            .filter((conversation) => (conversation.conversation_attributes?.unread_provider_messages_count ?? 0) > 0)
            .map((conversation) => ({ conversation, sortDate: conversation.conversation_update_datetime })),
          ...unreadProviderPairThreads.map(({ thread, client, lastMessageSentAt }) => ({
            providerPairThread: thread,
            client,
            sortDate: lastMessageSentAt,
          })),
        ],
        ({ sortDate }) => -new Date(sortDate ?? 0).getTime(),
      ),
    [dashboardConversations, unreadProviderPairThreads],
  )
  const onMessageItemClicked = (
    client: ClientListClientObject,
    conversation: ConversationObject,
    participant: MessengerParticipant,
  ) => {
    dispatch(setSelectedClientId(client.id))
    dispatch(setSelectedConversation(conversation.conversation_id))
    setInitialMessageParticipant(participant)
    setMsgPopoverOpen(true)
  }

  const closePopover = () => {
    dispatch(clearSelectedConversation())
    setMsgPopoverOpen(false)
  }

  // Open the clicked pair thread, not the client's default patient thread, which opening would mark read.
  const onProviderPairItemClicked = (clientId: string, threadId: string) => {
    dispatch(setSelectedClientId(clientId))
    dispatch(setLastConversationClientId(clientId))
    dispatch(clearSelectedConversation())
    dispatch(setPendingInitialThreadId(threadId))
    navigate(MESSENGER.route)
  }

  const refreshMessages = () => {
    refetchConversations()
    // RTK Query 1.x throws when refetching a query that was skipped.
    if (!isProviderPairQuerySkipped) {
      refetchProviderPairConversations()
    }
  }

  const renderClientListItem = (conversation: ConversationObject) => {
    const clientId = getAnchorId(conversation)
    const client = Array.isArray(clients) && clients.find(({ id }) => id === clientId)
    if (!client) {
      // in the case where we have a conversation but no matching client
      return
    }
    const isAnchoredConversation =
      conversation.anchor_patient_lyra_id != null &&
      conversation.anchor_patient_lyra_id !== conversation.patient_lyra_id
    const isCaregiverConversation =
      isDBTTeensEnabled &&
      client.program_name === ProgramNames.DialecticalBehaviorTherapyTeens &&
      isAnchoredConversation
    const clientFullName = getClientFullName(client, isPreferredNameEnabled)
    const caregiverFirstName = caregiverFirstNames.get(`${client.id}:${conversation.patient_lyra_id}`)
    const clientName = isCaregiverConversation
      ? `${caregiverFirstName || 'Caregiver'} (${clientFullName}’s caregiver)`
      : clientFullName
    const initials = getInitials(clientFullName)
    return (
      <div
        className={styles['dashlet-item-container']}
        data-test-id={`MessagesDashlet-message-item-${client.first_name}`}
        key={conversation.conversation_id}
      >
        <button
          type='button'
          className={styles['dashlet-item-content']}
          key={conversation.conversation_id}
          onClick={() =>
            onMessageItemClicked(
              client,
              conversation,
              isCaregiverConversation ? MessengerParticipant.GUARDIAN : MessengerParticipant.PATIENT,
            )
          }
        >
          <div className={styles.left}>
            <Avatar ltStyle size={35}>
              {initials}
            </Avatar>
            <button
              type='button'
              className={styles.clientName}
              data-test-id={`MessagesDashlet-client-name-${isCaregiverConversation ? 'caregiver' : 'client'}-${client.id}`}
              onClick={() => {
                dispatch(setSelectedClientId(client.id))
                navigate(CLIENT_HOME.route)
              }}
            >
              {clientName}
            </button>
            {primaryClientIds.has(client.id) && atRiskClientIds.has(client.id) && (
              <IconBadge
                testID={`MessagesDashlet-risk-badge-${client.id}`}
                text={riskBadgeText}
                variant={BadgeVariant.ALERT}
                shape={BadgeShape.RECT}
              />
            )}
            {inactiveClients?.some((c: any) => c.id === client.id) && (
              <div className={styles['inactive-tag']} data-test-id='MessagesDashlet-inactiveTag'>
                inactive
              </div>
            )}
          </div>
          <div className={styles.right}>
            {showOverdueMessageFlag && conversation?.overdue && (
              <div className={styles['overdue-tag']} data-test-id='MessagesDashlet-overdueTag'>
                overdue
              </div>
            )}
            <Label
              textColor={styles.x_white}
              backgroundColor={styles.x_alert}
              borderRadius='6px'
              padding='0 7px'
              // @ts-expect-error TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
              text={conversation?.conversation_attributes?.unread_provider_messages_count}
            />
          </div>
        </button>
      </div>
    )
  }

  return (
    <div className={styles['dashlet-container']} data-test-id='MessagesDashlet-container'>
      {msgPopoverOpen && (
        <div className={styles['messenger-container']}>
          {
            <MessagesPopover
              closePopover={closePopover}
              entryPointOverride={ChatEntryPoint.TO_DO_LIST}
              hideThreadList
              initialParticipant={initialMessageParticipant}
            />
          }
        </div>
      )}
      <div style={{ marginBottom: '20px', width: '100%', height: msgPopoverOpen ? '400px' : 'auto' }}>
        <Dashlet
          title={`Unread messages${clientsFetched ? ` (${unreadCount})` : ''}`}
          buttons={
            <RefreshButton
              data-test-id='MessagesDashlet-refreshButton'
              size='20'
              isLoading={isRefreshingMessages}
              onClick={refreshMessages}
            />
          }
          isLoading={
            isLoadingClients ||
            (!conversationsFetched && isEmpty(conversations)) ||
            isLoadingProviderPairConversations ||
            ((hasUnreadDBTTeensCaregiverConversation || unreadProviderPairConversations.length > 0) &&
              isLoadingEpisodeParticipantRosters)
          }
        >
          {unreadCount === 0 ? (
            <div className={styles['empty-dashlet']}>No new messages</div>
          ) : (
            unreadItems.map((item) => {
              if ('conversation' in item) {
                return renderClientListItem(item.conversation)
              }
              const { providerPairThread, client } = item
              return (
                <ProviderPairDashletItem
                  key={providerPairThread.conversationId}
                  conversationId={providerPairThread.conversationId}
                  counterpartName={providerPairThread.counterpart.displayName}
                  clientFullName={getClientFullName(client, isPreferredNameEnabled)}
                  unreadCount={providerPairThread.unreadCount}
                  onPress={() => onProviderPairItemClicked(client.id, providerPairThread.id)}
                />
              )
            })
          )}
        </Dashlet>
      </div>
    </div>
  )
}

export default MessagesDashlet
